// @givmo/board — disconnect a member's Givmo account from their seat (CBG-SEC2; D-CBG-17 decision 2).
//
// A joined seat stays joined when its email changes; disconnecting is this separate, recorded act, an
// Owner's, with a reason the roster's history keeps and a notice to both the account's address and the
// seat's. It's for when the wrong account joined (a shared inbox, an assistant) or the member's account
// is gone. Nothing they did is undone: their acts keep naming their seat and the account that acted.
// The seat can then be invited again. The words are Jonathan's (copy round 2, D-CBG-18): "We'll
// email them to say so." names no number of addresses, because a member who joined with the seat's
// own address has only one.

import { useState } from 'react';
import { Button, Dialog, ErrorMessage, TextField } from '@givmo/design-system';
import type { OrgBoardMemberOut } from '@givmo/api-client';
import { FieldNote } from '../../components/field-note';

export interface ReleaseAccountDialogProps {
  seat: OrgBoardMemberOut;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export function ReleaseAccountDialog({
  seat,
  pending,
  error,
  onCancel,
  onConfirm,
}: ReleaseAccountDialogProps) {
  const [reason, setReason] = useState('');
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !pending && onCancel()}>
      <Dialog.Content
        aria-label="Disconnect a member's account"
        className="flex flex-col gap-16"
        testId="roster-release-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Disconnect {seat.display_name}’s Givmo account?
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          They won’t be able to read or sign for this board with that account until they join again
          from a new invitation. We’ll email them to say so.
        </p>
        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          Nothing they did is removed: everything recorded with their name stays exactly as it is.
        </p>

        <TextField
          label="Why you’re disconnecting it"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          testId="roster-release-reason"
        />
        <FieldNote>Kept in this seat’s history.</FieldNote>

        {error !== null ? <ErrorMessage testId="roster-release-error">{error}</ErrorMessage> : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={pending}
            onClick={onCancel}
            testId="roster-release-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            loading={pending}
            disabled={reason.trim().length === 0 || pending}
            onClick={() => onConfirm(reason.trim())}
            testId="roster-release-confirm"
          >
            Disconnect account
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
